import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import { questionEditorService } from "../../service/assessSections/questionEditorService";

export const QuestionMetaContext = createContext(null);

const getSection = (metaData, key) => {
  if (!metaData) return [];

  if (Array.isArray(metaData)) {
    return metaData.find((item) => Array.isArray(item?.[key]))?.[key] || [];
  }

  if (Array.isArray(metaData?.[key])) {
    return metaData[key];
  }

  return [];
};

export function QuestionMetaProvider({ children }) {
  const [rawMetaDataByScope, setRawMetaDataByScope] = useState({});
  const [activeMetaScope, setActiveMetaScope] = useState("library");
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [metaError, setMetaError] = useState("");

  const mountedRef = useRef(true);

  React.useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getMetaScopeKey = ({ scope = "create", includeAssessments = false } = {}) => {
    if (scope === "library") return "library";

    return includeAssessments ? "create-with-assessments" : "create";
  };

  const loadQuestionMetaData = useCallback(
    async ({
      scope = "create",
      includeAssessments = false,
      forceRefresh = false,
    } = {}) => {
      const scopeKey = getMetaScopeKey({ scope, includeAssessments });

      if (!forceRefresh && rawMetaDataByScope[scopeKey]) {
        setActiveMetaScope(scopeKey);
        return rawMetaDataByScope[scopeKey];
      }

      if (mountedRef.current) {
        setLoadingMeta(true);
        setMetaError("");
      }

      try {
        const response =
          scope === "library"
            ? await questionEditorService.getQuestionLibraryMetadata({
              forceRefresh,
            })
            : await questionEditorService.getQuestionMetadata({
              forceRefresh,
              includeAssessments,
            });

        const data = response?.data || {};

        if (mountedRef.current) {
          setRawMetaDataByScope((prev) => ({
            ...prev,
            [scopeKey]: data,
          }));

          setActiveMetaScope(scopeKey);
        }

        return data;
      } catch (error) {
        if (mountedRef.current) {
          setMetaError("Failed to load question metadata.");
        }

        return {};
      } finally {
        if (mountedRef.current) {
          setLoadingMeta(false);
        }
      }
    },
    [rawMetaDataByScope]
  );

  const rawMetaData = rawMetaDataByScope[activeMetaScope] || {};
  const getMetaByScope = useCallback(
    ({ scope = "create", includeAssessments = false } = {}) => {
      const scopeKey = getMetaScopeKey({ scope, includeAssessments });
      const scopedRawMetaData = rawMetaDataByScope[scopeKey] || {};

      return {
        categories: getSection(scopedRawMetaData, "categories"),
        languages: getSection(scopedRawMetaData, "languages"),
        types: getSection(scopedRawMetaData, "types"),
        subjects: getSection(scopedRawMetaData, "subjects"),
        authors: getSection(scopedRawMetaData, "authors"),
        sources: getSection(scopedRawMetaData, "sources"),
        years: getSection(scopedRawMetaData, "years"),
        difficulties: getSection(scopedRawMetaData, "difficulty_types"),
        grades: getSection(scopedRawMetaData, "grades"),
        terms: getSection(scopedRawMetaData, "terms"),
        chapters: getSection(scopedRawMetaData, "chapters"),
        privileges: getSection(scopedRawMetaData, "privileges"),
        creators: getSection(scopedRawMetaData, "creators"),
        passages: getSection(scopedRawMetaData, "passages"),
        passageTypes: getSection(scopedRawMetaData, "passage_types"),
        parts: getSection(scopedRawMetaData, "parts"),
        sections: getSection(scopedRawMetaData, "sections"),
        answerTypes: getSection(scopedRawMetaData, "answer_types"),
        assessments: getSection(scopedRawMetaData, "assessments"),
        assessmentParts: getSection(scopedRawMetaData, "assessment_parts"),
        assessmentSections: getSection(scopedRawMetaData, "assessment_sections"),
      };
    },
    [rawMetaDataByScope]
  );

  const meta = useMemo(
    () => ({
      categories: getSection(rawMetaData, "categories"),
      languages: getSection(rawMetaData, "languages"),
      types: getSection(rawMetaData, "types"),
      subjects: getSection(rawMetaData, "subjects"),
      authors: getSection(rawMetaData, "authors"),
      sources: getSection(rawMetaData, "sources"),
      years: getSection(rawMetaData, "years"),
      difficulties: getSection(rawMetaData, "difficulty_types"),
      grades: getSection(rawMetaData, "grades"),
      terms: getSection(rawMetaData, "terms"),
      chapters: getSection(rawMetaData, "chapters"),
      privileges: getSection(rawMetaData, "privileges"),
      creators: getSection(rawMetaData, "creators"),
      passages: getSection(rawMetaData, "passages"),
      passageTypes: getSection(rawMetaData, "passage_types"),
      parts: getSection(rawMetaData, "parts"),
      sections: getSection(rawMetaData, "sections"),
      answerTypes: getSection(rawMetaData, "answer_types"),
      assessments: getSection(rawMetaData, "assessments"),
      assessmentParts: getSection(rawMetaData, "assessment_parts"),
      assessmentSections: getSection(rawMetaData, "assessment_sections"),
    }),
    [rawMetaData]
  );

  const createLookupMap = useCallback((list, idKey, labelKey) => {
    return new Map(
      (list || [])
        .filter((item) => item?.[idKey] !== undefined && item?.[idKey] !== null)
        .map((item) => [String(item[idKey]), item[labelKey] || "N/A"])
    );
  }, []);

  const lookupMaps = useMemo(
    () => ({
      categories: createLookupMap(meta.categories, "category_id", "name"),
      difficulties: createLookupMap(meta.difficulties, "difficulty_id", "difficulty_type"),
      types: createLookupMap(meta.types, "type_id", "qtype"),
      subjects: createLookupMap(meta.subjects, "subject_id", "subject"),
      languages: createLookupMap(meta.languages, "language_id", "language"),
      grades: createLookupMap(meta.grades, "grade_id", "grade"),
      authors: createLookupMap(meta.authors, "author_id", "author"),
      sources: createLookupMap(meta.sources, "source_id", "source"),
      years: createLookupMap(meta.years, "year_id", "year"),
      terms: createLookupMap(meta.terms, "term_id", "term"),
      chapters: createLookupMap(meta.chapters, "chapter_id", "chapter"),
    }),
    [createLookupMap, meta]
  );

  const getFromMap = useCallback((map, id) => {
    if (id === undefined || id === null || id === "") return "N/A";
    return map.get(String(id)) || "N/A";
  }, []);

  const helpers = useMemo(
    () => ({
      getCategoryName: (id) => getFromMap(lookupMaps.categories, id),
      getDifficultyName: (id) => getFromMap(lookupMaps.difficulties, id),
      getTypeName: (id) => getFromMap(lookupMaps.types, id),
      getSubjectName: (id) => getFromMap(lookupMaps.subjects, id),
      getLanguageName: (id) => getFromMap(lookupMaps.languages, id),
      getGradeName: (id) => getFromMap(lookupMaps.grades, id),
      getAuthorName: (id) => getFromMap(lookupMaps.authors, id),
      getSourceName: (id) => getFromMap(lookupMaps.sources, id),
      getYearName: (id) => getFromMap(lookupMaps.years, id),
      getTermName: (id) => getFromMap(lookupMaps.terms, id),
      getChapterName: (id) => getFromMap(lookupMaps.chapters, id),
    }),
    [lookupMaps, getFromMap]
  );

  const value = useMemo(
    () => ({
      rawMetaData,
      rawMetaDataByScope,
      activeMetaScope,
      meta,
      getMetaByScope,
      ...helpers,
      loadingMeta,
      metaError,
      loadQuestionMetaData,
    }),
    [
      rawMetaData,
      rawMetaDataByScope,
      activeMetaScope,
      meta,
      getMetaByScope,
      helpers,
      loadingMeta,
      metaError,
      loadQuestionMetaData,
    ]
  );

  return (
    <QuestionMetaContext.Provider value={value}>
      {children}
    </QuestionMetaContext.Provider>
  );
}
export function useQuestionMeta() {
  const context = useContext(QuestionMetaContext);

  if (!context) {
    throw new Error("useQuestionMeta must be used inside QuestionMetaProvider");
  }

  return context;
}